import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import PageHead from "@/components/PageHead";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Ticket, Copy, RefreshCw, Send, Ban, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

type Invite = {
  id: string;
  code: string;
  email: string;
  status: "active" | "redeemed" | "expired" | "revoked";
  issued_at: string;
  expires_at: string;
  redeemed_at: string | null;
  revoked_at: string | null;
  revoke_reason: string | null;
  notes: string | null;
};

const fmt = (s?: string | null) =>
  s ? new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

const statusBadge = (s: Invite["status"]) => {
  switch (s) {
    case "active": return <Badge>Active</Badge>;
    case "redeemed": return <Badge variant="secondary">Redeemed</Badge>;
    case "expired": return <Badge variant="outline">Expired</Badge>;
    case "revoked": return <Badge variant="destructive">Revoked</Badge>;
  }
};

export default function AdminCompInvites() {
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active");
  const [query, setQuery] = useState("");

  const [email, setEmail] = useState("");
  const [days, setDays] = useState("7");
  const [notes, setNotes] = useState("");
  const [issuing, setIssuing] = useState(false);

  const [revokeTarget, setRevokeTarget] = useState<Invite | null>(null);
  const [revokeReason, setRevokeReason] = useState("");
  const [revoking, setRevoking] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("complimentary_invites" as any)
      .select("*")
      .order("issued_at", { ascending: false })
      .limit(500);
    if (error) {
      console.error(error);
      toast.error("Failed to load invites");
    } else {
      setInvites(((data ?? []) as unknown) as Invite[]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return invites.filter((i) => {
      if (tab !== "all" && i.status !== tab) return false;
      if (!q) return true;
      return i.email.toLowerCase().includes(q) || i.code.toLowerCase().includes(q);
    });
  }, [invites, tab, query]);

  const onIssue = async (e: React.FormEvent) => {
    e.preventDefault();
    setIssuing(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-issue-comp-invite", {
        body: { email: email.trim().toLowerCase(), expires_in_days: Number(days) || 7, notes: notes || null },
      });
      if (error || !data?.ok) {
        const err = (data?.error as string) || (error?.message ?? "Failed to issue");
        if (err === "active_invite_exists") {
          toast.error("This email already has an active invite — revoke it first.");
        } else if (err === "invalid_email") {
          toast.error("Please enter a valid email.");
        } else if (err === "forbidden") {
          toast.error("Admins only.");
        } else {
          toast.error(`Couldn't issue invite: ${err}`);
        }
        return;
      }
      toast.success(`Invite ${data.invite.code} sent to ${email}`);
      setEmail(""); setNotes("");
      await load();
    } finally {
      setIssuing(false);
    }
  };

  const copyLink = (code: string) => {
    const link = `${window.location.origin}/redeem?code=${encodeURIComponent(code)}`;
    navigator.clipboard.writeText(link);
    toast.success("Redemption link copied");
  };

  const onRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-revoke-comp-invite", {
        body: { id: revokeTarget.id, reason: revokeReason || null },
      });
      if (error || !data?.ok) {
        toast.error(`Couldn't revoke: ${data?.error ?? error?.message}`);
        return;
      }
      toast.success("Invite revoked");
      setRevokeTarget(null); setRevokeReason("");
      await load();
    } finally {
      setRevoking(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHead
        title="Complimentary Invites | WeKIT™ Admin"
        description="Issue, track, and revoke single-use complimentary invites."
        path="/admin/invites"
        noIndex
      />

      <header className="border-b">
        <div className="container py-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-sm text-muted-foreground">WeKIT™ Admin</p>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Ticket className="h-7 w-7" /> Complimentary Invites
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              One unique code, one email, one redemption. Replaces shared 100%-off coupons for free access.
            </p>
          </div>
          <Button variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </header>

      <main className="container py-8 space-y-8">
        {/* Issue form */}
        <Card className="rounded-3xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="h-4 w-4" /> Issue a new invite
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={onIssue} className="grid gap-4 md:grid-cols-[1fr_140px_auto] items-end">
              <div className="md:col-span-3 grid gap-4 md:grid-cols-[1fr_140px_auto]">
                <div>
                  <Label htmlFor="email">Recipient email</Label>
                  <Input
                    id="email" type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="student@example.com" className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="days">Expires in (days)</Label>
                  <Input
                    id="days" type="number" min={1} max={90} value={days}
                    onChange={(e) => setDays(e.target.value)} className="mt-1"
                  />
                </div>
                <Button type="submit" disabled={issuing} className="h-10">
                  {issuing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                  Issue & email
                </Button>
              </div>
              <div className="md:col-span-3">
                <Label htmlFor="notes">Internal note (optional)</Label>
                <Textarea
                  id="notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. School partner cohort, batch 2026-A" rows={2} className="mt-1"
                />
              </div>
            </form>
          </CardContent>
        </Card>

        {/* List */}
        <Card className="rounded-3xl">
          <CardHeader>
            <CardTitle>All invites</CardTitle>
            <div className="flex gap-3 flex-wrap mt-2">
              <Input
                placeholder="Search email or code…" value={query}
                onChange={(e) => setQuery(e.target.value)} className="max-w-xs"
              />
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={tab} onValueChange={setTab} className="space-y-4">
              <TabsList>
                <TabsTrigger value="active">Active</TabsTrigger>
                <TabsTrigger value="redeemed">Redeemed</TabsTrigger>
                <TabsTrigger value="expired">Expired</TabsTrigger>
                <TabsTrigger value="revoked">Revoked</TabsTrigger>
                <TabsTrigger value="all">All</TabsTrigger>
              </TabsList>
              <TabsContent value={tab}>
                {loading ? (
                  <div className="py-8 text-center text-muted-foreground">Loading…</div>
                ) : filtered.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground">No invites match.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Code</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Issued</TableHead>
                          <TableHead>Expires</TableHead>
                          <TableHead>Redeemed</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filtered.map((i) => (
                          <TableRow key={i.id}>
                            <TableCell className="font-mono text-xs">{i.code}</TableCell>
                            <TableCell className="text-sm">{i.email}</TableCell>
                            <TableCell>{statusBadge(i.status)}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{fmt(i.issued_at)}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{fmt(i.expires_at)}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{fmt(i.redeemed_at)}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button size="icon" variant="ghost" title="Copy redeem link"
                                  onClick={() => copyLink(i.code)}>
                                  <Copy className="h-4 w-4" />
                                </Button>
                                <Button size="icon" variant="ghost" title="Open redeem page" asChild>
                                  <a href={`/redeem?code=${encodeURIComponent(i.code)}`} target="_blank" rel="noreferrer">
                                    <ExternalLink className="h-4 w-4" />
                                  </a>
                                </Button>
                                {i.status === "active" && (
                                  <Button size="icon" variant="ghost" title="Revoke"
                                    onClick={() => setRevokeTarget(i)}>
                                    <Ban className="h-4 w-4 text-destructive" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </main>

      <Dialog open={!!revokeTarget} onOpenChange={(o) => !o && setRevokeTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke invite {revokeTarget?.code}</DialogTitle>
            <DialogDescription>
              This will permanently disable the code. The recipient will see a "revoked" error if they try to redeem.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="reason">Reason (optional, audit-logged)</Label>
            <Textarea id="reason" value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)} rows={3} className="mt-1" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={onRevoke} disabled={revoking}>
              {revoking && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Revoke
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
